import type { HubState } from '../state';
import {
    AppState,
    ClickedHubPersonaState,
    HubPersonasMap,
    UnifiedGroupsMap,
    getActiveLeftNavItemId,
    getAllHubPersonasAcrossFolders,
    getFavorites,
    isActiveLeftNavItemDeletedItems,
    isInEVNavigationMode,
    EmailToPDLsMapType,
    CheckedHubPersonaDetails,
} from '../../appstate';
import {
    HubPersona,
    FavoriteEntity,
    createSelector,
    createMapOfSet,
    PersonType,
    uniqBy,
    DeletedItemsSubViewType,
    getUniqueEmailAddressesFromPersona,
    flatten,
    uniq,
    intersection,
    PrivateDistributionList,
} from '../../shared';
import {
    getFirstHubPersonaIdInPersonaList,
    getDisplayedItemHubPersonaIds,
} from './personaListSelector';
import { getPersonaForFavoritePersona } from './personaListSelectorUtils';

export type { HubPersona };

/**
 * Returns a list of one or more HubPersonaIds, indicating the Persona(s) currently in focus.
 */
export const getCurrentHubPersonaIds = createSelector<
    AppState,
    HubState,
    ClickedHubPersonaState,
    CheckedHubPersonaDetails[],
    string,
    string[]
>(
    'getCurrentPersonaIds',
    [
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        (appState: AppState) => appState.view.navigationState.personaListCheckedHubPersonaDetails,
        getFirstHubPersonaIdInPersonaList,
    ],

    (clickedHubPersona, personaListCheckedHubPersonaDetails, firstHubPersonaIdInPersonaList) => {
        if (personaListCheckedHubPersonaDetails && personaListCheckedHubPersonaDetails.length > 0) {
            return personaListCheckedHubPersonaDetails.map(
                checkedHubPersonaDetails => checkedHubPersonaDetails.hubPersonaId
            );
        } else if (clickedHubPersona) {
            return [clickedHubPersona.hubPersonaId];
        }

        return firstHubPersonaIdInPersonaList ? [firstHubPersonaIdInPersonaList] : [];
    }
);

const getCurrentHubPersonasList = createSelector<
    AppState,
    HubState,
    string[],
    HubPersonasMap,
    UnifiedGroupsMap,
    HubPersona[]
>(
    'getCurrentHubPersonasList',
    [
        getCurrentHubPersonaIds,
        appState => appState.data.personas,
        appState => appState.data.groupsHubState.unifiedGroupsMap,
    ],
    (currentHubPersonaIds, personasMap, unifiedGroupsMap) =>
        currentHubPersonaIds
            .map(hubPersonaId => personasMap[hubPersonaId] || unifiedGroupsMap[hubPersonaId])
            .filter(persona => !!persona)
);

const getCurrentNavigationPersonas = createSelector<AppState, HubState, HubPersona, HubPersona[]>(
    'getCurrentNavigationPersonas',
    [(appState: AppState) => appState.data.hubState.currentNavigationPersonaState.currentPersona],
    currentNavigationPersona => {
        return [currentNavigationPersona];
    }
);

const getCurrentDeletedPersonas = createSelector<
    AppState,
    HubState,
    string[],
    HubPersona[],
    HubPersona[],
    DeletedItemsSubViewType,
    HubPersona[]
>(
    'getCurrentDeletedPersonas',
    [
        getCurrentHubPersonaIds,
        (appState: AppState) => appState.data.hubState.deletedPersonasState.deletedPersonas,
        (appState: AppState) => appState.data.hubState.deletedPersonasState.recoverablePersonas,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view && hubState.view.deletedItemsFolderState.deletedItemsSubViewType,
    ],
    (currentHubPersonaIds, deletedPersonas, recoverablePersonas, deletedItemsSubViewType) => {
        const currentHubPersonaIdsMap = createMapOfSet(currentHubPersonaIds);
        const personas =
            deletedItemsSubViewType === DeletedItemsSubViewType.RecoverableContactsFolder
                ? recoverablePersonas
                : deletedPersonas;
        return personas.filter(persona => currentHubPersonaIdsMap[persona.hubPersonaId]);
    }
);

const getCurrentFavoritePersonas = createSelector<
    AppState,
    HubState,
    string[],
    FavoriteEntity[],
    HubPersonasMap,
    UnifiedGroupsMap,
    HubPersona[],
    HubPersona[]
>(
    'getCurrentFavoritePersonas',
    [
        getCurrentHubPersonaIds,
        getFavorites,
        appState => appState.data.personas,
        appState => appState.data.groupsHubState.unifiedGroupsMap,
        getAllHubPersonasAcrossFolders,
    ],
    (
        currentHubPersonaIds,
        favoritePersonas,
        personasMap,
        unifiedGroupsMap,
        allContactFolderPersonas
    ) => {
        const currentHubPersonaIdsMap = createMapOfSet(currentHubPersonaIds);
        return favoritePersonas
            .filter(persona => currentHubPersonaIdsMap[persona.hubPersonaId])
            .map(
                favorite =>
                    getPersonaForFavoritePersona(
                        favorite,
                        personasMap,
                        unifiedGroupsMap,
                        allContactFolderPersonas
                    ) || favorite
            );
    }
);

/**
 * Returns a list of one or more Personas, indicating the Persona(s) currently in focus.
 */
export const getCurrentHubPersonas = createSelector<
    AppState,
    HubState,
    AppState,
    HubState,
    string,
    boolean,
    HubPersona[]
>(
    'getCurrentPersonas',
    [
        appState => appState,
        (_appState, hubState) => hubState,
        getActiveLeftNavItemId,
        isInEVNavigationMode,
    ],
    (appState, hubState, activeLeftItemId, isInEVNavigationMode) => {
        if (isActiveLeftNavItemDeletedItems(appState)) {
            return getCurrentDeletedPersonas(appState, hubState);
        }
        if (isInEVNavigationMode) {
            return getCurrentNavigationPersonas(appState, hubState);
        }
        // Merge mailbox contacts and favorites, and remove duplicates. Duplicates must be removed to prevent
        // newly added favorite to appear as duplicate.
        return uniqBy(
            [
                ...getCurrentHubPersonasList(appState, hubState),
                ...getCurrentFavoritePersonas(appState, hubState),
            ],
            persona => persona.hubPersonaId
        );
    }
);

/**
 * Returns the hubPersonaId of the persona currently in focus.
 * If multiple personas are in focus, undefined is returned.
 */
export const getCurrentHubPersonaId = createSelector<AppState, HubState, string[], string>(
    'getCurrentPersonaId',
    [getCurrentHubPersonaIds],
    currentHubPersonaIds => {
        return currentHubPersonaIds && currentHubPersonaIds.length === 1
            ? currentHubPersonaIds[0]
            : undefined;
    }
);

/**
 * Returns the Persona currently in focus.
 * If multiple personas are in focus, undefined is returned.
 */
export const getCurrentHubPersona = createSelector<
    AppState,
    HubState,
    HubPersona[],
    HubPersonasMap,
    UnifiedGroupsMap,
    HubPersona[],
    HubPersona[],
    boolean,
    HubPersona
>(
    'getCurrentHubPersona',
    [
        getCurrentHubPersonas,
        (appState: AppState) => appState.data.personas,
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getAllHubPersonasAcrossFolders,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.moveContactsState.personas : [],
        isActiveLeftNavItemDeletedItems,
    ],
    (
        currentPersonas,
        hubPersonasMap,
        unifiedGroupsMap,
        allContactFolderPersonas,
        personasInMove,
        isInDeletedItems
    ) => {
        const singleCurrentPersona =
            currentPersonas && currentPersonas.length === 1 ? currentPersonas[0] : undefined;

        if (isInDeletedItems) {
            return undefined;
        }

        if (!singleCurrentPersona) {
            return undefined;
        }

        if (
            personasInMove.length &&
            personasInMove.some(
                persona => persona.hubPersonaId === singleCurrentPersona.hubPersonaId
            )
        ) {
            return undefined;
        }

        // If the current persona is a Favorite, try to return a contact it exists
        if (
            singleCurrentPersona.type === PersonType.FavoritePerson ||
            singleCurrentPersona.type === PersonType.FavoritePrivateDistributionList
        ) {
            return (
                getPersonaForFavoritePersona(
                    singleCurrentPersona,
                    hubPersonasMap,
                    unifiedGroupsMap,
                    allContactFolderPersonas
                ) || singleCurrentPersona
            );
        }

        return singleCurrentPersona;
    }
);

/**
 * Returns whether all displayed personas are checked
 */
export const areAllItemsChecked = createSelector<
    AppState,
    HubState,
    string[],
    HubPersona[],
    boolean
>(
    'areAllItemsChecked',
    [getDisplayedItemHubPersonaIds, getCurrentHubPersonas],
    (displayedItemHubPersonaIds, currentHubPersonas) => {
        return (
            displayedItemHubPersonaIds &&
            currentHubPersonas &&
            displayedItemHubPersonaIds.length === currentHubPersonas.length
        );
    }
);

/**
 * Returns all pdls all of the selected personas are part of
 */
export const getCurrentHubPersonasRelatedPDLs = createSelector<
    AppState,
    HubState,
    HubPersona[],
    EmailToPDLsMapType,
    HubPersonasMap,
    PrivateDistributionList[]
>(
    'getCurrentHubPersonasRelatedPDLs',
    [
        getCurrentHubPersonas,
        appState => appState.data.hubState.emailToPDLsMapState.emailToPDLsMap,
        appState => appState.data.personas,
    ],
    (currentHubPersonas, emailToPDLsMap, hubPersonasMap) => {
        const emailsOfPersonas = currentHubPersonas.map(persona =>
            getUniqueEmailAddressesFromPersona(persona)
        );

        const listOfRelatedDLsPerEmail = emailsOfPersonas.map(emails =>
            uniq(flatten(emails.map(email => emailToPDLsMap[email] || [])))
        );
        const uniqRelatedDLs =
            listOfRelatedDLsPerEmail.length !== 0
                ? listOfRelatedDLsPerEmail.reduce(intersection)
                : [];
        return uniqRelatedDLs
            .map(dlId => hubPersonasMap[dlId])
            .filter(
                dl => dl && dl.type === PersonType.PrivateDistributionList
            ) as PrivateDistributionList[];
    }
);

/**
 * Returns all pdls excluding the one where the selected personas are part of
 */
export const getCurrentHubPersonasUnrelatedPDLs = createSelector<
    AppState,
    HubState,
    HubPersona[],
    HubPersonasMap,
    PrivateDistributionList[]
>(
    'getCurrentHubPersonasUnrelatedPDLs',
    [getCurrentHubPersonasRelatedPDLs, appState => appState.data.personas],
    (relatedPDLs, hubPersonasMap) => {
        const relatedPDLsMap = createMapOfSet(relatedPDLs.map(pdl => pdl.hubPersonaId));
        return Object.keys(hubPersonasMap)
            .filter(
                hubPersonaId =>
                    hubPersonasMap[hubPersonaId].type === PersonType.PrivateDistributionList &&
                    !relatedPDLsMap[hubPersonaId]
            )
            .map(pdlPersonaId => hubPersonasMap[pdlPersonaId] as PrivateDistributionList);
    }
);
